/*
  ヘッダーメニューの開閉

  実装仕様
  #1 JSが要るのは閉じるときだけ。details は open を外した瞬間に中身を隠すので、
     CSSだけだと戻りのアニメーションが再生されない。
     summary の既定動作を止め、パネルのフェードが終わってから open を外す
  #2 開くときは何もしない。ブラウザに任せ、CSS側のアニメーションがそのまま走る
  #3 animationend が来ない場合（裏のタブに居る等）に開いたままにならないよう、
     時間切れでも必ず閉じる
  #4 動きを減らす設定のときは待たずに閉じる
  #5 Escape でも閉じる。全面を覆うパネルなので、キーボードだけで抜けられるようにする
  #6 JSが読めなかった場合は details の既定動作のまま。
     アニメーションが出ないだけで、開閉そのものは動く
  #7 重ねるページでは、文字色を白とブランドカラーに切り替える
     （2026年9月23日 PM指定）。ここが決めるのは「どの項目の下に、どの絵の、どこが
     来ているか」だけで、明るさの測り方と境目は assets/custom luminance.js が持つ。
     塗るのは項目単位（1文字が縦に割れて半分白 半分紺になるのを避けるため）だが、
     **色は4項目で1つにそろえる**（2026年9月29日 PM指摘）。項目ごとに決めていたころは
     絵の明るいところが来た順に1つずつ反転し、ロゴだけ先に変わって見えた。
     4項目それぞれの下の明るさを平均して、そろって切り替える
  #10 先頭の絵（重ねるセクションの中の data c header bg）がヘッダーの下を抜けたら、
     ヘッダーに地を敷いて4項目ともブランドカラーにする（2026年9月29日 PM指摘）。
     地を持たないままだと、絵のあとに続く本文の上に項目の文字が重なった
  #11 Focal のポップアップ（ニュースレターなど）が開いてページを止めている
     あいだ（html に lock all）は塗り直さない。止まっているあいだヘッダーの貼り付きが外れ、
     ヘッダーがページと一緒に上へ流れるので、位置から判定すると絵の上にいると誤る。
     閉じて lock all が外れたら塗り直す
  #8 塗り直すのはスクロールしたときと、下の絵が入れ替わったとき。
     常時まわす輪は持たない。地が動くのはこの2つの場合しかない
  #9 メニューが開いているあいだは測らずにブランドカラーで固定する。
     面は白地なので、暗い絵の上で白にしたままだとロゴも x も消える。
     閉じるアニメーションの最中も open が残っているので、そのまま固定が続く
*/
(function () {
  'use strict';

  var CLOSING = 'data-c-menu-closing';
  var TIMEOUT = 1000;

  /* 色を決める単位。ロゴと、右の3つ（EN / Store Location / MENU） */
  var TONE_ITEMS = '.c-header__logo, .c-header__link';
  var TONE = 'data-c-header-tone';
  var BG = '[data-c-header-bg]';
  var OVERLAY = '[data-c-header-overlay]';
  var SOLID = 'data-c-header-solid';
  var LOCKED = 'lock-all';

  var watchers = [];
  var frame = 0;

  function schedule() {
    if (frame) {
      return;
    }

    frame = requestAnimationFrame(function () {
      frame = 0;
      paintTone();
    });
  }

  /*
    その範囲の下にある地の明るさ（0〜1）。実装仕様 #7

    掛かっているものが複数あれば、あとに書いてあるほうを採る（手前に描かれるため）。
    何も掛かっていなければページの地＝明るい扱い
  */
  function valueUnder(rect) {
    var lum = window.cLuminance;
    var value = 1;

    if (!lum) {
      return value;
    }

    Array.prototype.forEach.call(document.querySelectorAll(BG), function (bg) {
      var box = bg.getBoundingClientRect();

      if (
        box.bottom <= rect.top ||
        box.top >= rect.bottom ||
        box.right <= rect.left ||
        box.left >= rect.right
      ) {
        return;
      }

      var kind = bg.getAttribute('data-c-header-bg');

      if (kind === 'dark') {
        value = 0;
        return;
      }

      if (kind === 'light') {
        value = 1;
        return;
      }

      /* 絵。重ねて置いてある作り（ヒーロー）では、出ている1枚を見る */
      var image = bg.querySelector('[data-active] img') || bg.querySelector('img');
      var src = image && (image.currentSrc || image.getAttribute('src'));

      if (!src) {
        value = 1;
        return;
      }

      var table = lum.known(src);

      /* まだ測れていない。測り終わってから塗り直す */
      if (!table) {
        lum.grid(src, schedule);
        value = 1;
        return;
      }

      var frameBox = image.getBoundingClientRect();

      if (!frameBox.width || !frameBox.height) {
        value = 1;
        return;
      }

      /* その項目の真下に来ている範囲を、絵の中の割合に直す */
      var x0 = Math.max(0, (rect.left - frameBox.left) / frameBox.width);
      var x1 = Math.min(1, (rect.right - frameBox.left) / frameBox.width);
      var y0 = Math.max(0, (rect.top - frameBox.top) / frameBox.height);
      var y1 = Math.min(1, (rect.bottom - frameBox.top) / frameBox.height);

      if (x1 <= x0 || y1 <= y0) {
        return;
      }

      value = lum.inBox(table, frameBox.width / frameBox.height, x0, y0, x1, y1);
    });

    return value;
  }

  /*
    先頭の絵がまだヘッダーの下にあるか。実装仕様 #10

    重ねるセクションそのものではなく、その中で絵として名乗っているものの下端で見る。
    記事は本文ごと重ねるセクションになっているので、セクションの下端で見ると
    本文を読んでいるあいだもずっと地が無いままになる
  */
  function underImage(overlay, bar) {
    var bottom = -Infinity;

    Array.prototype.forEach.call(overlay.querySelectorAll(BG), function (bg) {
      bottom = Math.max(bottom, bg.getBoundingClientRect().bottom);
    });

    return bottom > bar.bottom;
  }

  function paintTone() {
    var header = document.querySelector('.c-header');

    /* ページが止まっているあいだは今の色のまま。実装仕様 #11 */
    if (!header || document.documentElement.classList.contains(LOCKED)) {
      return;
    }

    var lum = window.cLuminance;
    var overlay = document.querySelector(OVERLAY);
    var menuOpen = !!header.querySelector('[data-c-menu][open]');
    var items = Array.prototype.slice.call(header.querySelectorAll(TONE_ITEMS));
    var tone = 'light';
    var solid = true;

    /* 重ねないページと、メニューが開いているあいだは地ありの紺で固定。実装仕様 #9 */
    if (overlay && lum && !menuOpen && underImage(overlay, header.getBoundingClientRect())) {
      var sum = 0;

      items.forEach(function (item) {
        sum += valueUnder(item.getBoundingClientRect());
      });

      solid = false;
      tone = items.length && sum / items.length < lum.LIGHT_LIMIT ? 'dark' : 'light';
    }

    if (solid) {
      header.setAttribute(SOLID, '');
    } else {
      header.removeAttribute(SOLID);
    }

    items.forEach(function (item) {
      item.setAttribute(TONE, tone);
    });
  }

  /* 塗り直すきっかけ。実装仕様 #8 */
  function watchTone() {
    watchers.forEach(function (observer) {
      observer.disconnect();
    });

    watchers = [];

    if (!window.MutationObserver) {
      schedule();
      return;
    }

    var header = document.querySelector('.c-header');

    if (header) {
      var menu = new MutationObserver(schedule);
      menu.observe(header, { attributes: true, subtree: true, attributeFilter: ['open'] });
      watchers.push(menu);
    }

    /* ポップアップが閉じてページが動き出したら塗り直す。実装仕様 #11 */
    var lock = new MutationObserver(schedule);
    lock.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    watchers.push(lock);

    Array.prototype.forEach.call(document.querySelectorAll(BG), function (bg) {
      var swap = new MutationObserver(schedule);
      swap.observe(bg, { attributes: true, subtree: true, attributeFilter: ['data-active'] });
      watchers.push(swap);
    });

    schedule();
  }

  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule);
  window.addEventListener('load', schedule);

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function close(details) {
    if (!details.open || details.hasAttribute(CLOSING)) {
      return;
    }

    var panel = details.querySelector('[data-c-menu-panel]');

    if (!panel || prefersReducedMotion()) {
      details.open = false;
      return;
    }

    var settled = false;
    var timer = null;

    function done() {
      if (settled) {
        return;
      }

      settled = true;
      panel.removeEventListener('animationend', onAnimationEnd);
      window.clearTimeout(timer);
      details.removeAttribute(CLOSING);
      details.open = false;
    }

    function onAnimationEnd(event) {
      if (event.target === panel) {
        done();
      }
    }

    details.setAttribute(CLOSING, '');
    panel.addEventListener('animationend', onAnimationEnd);
    timer = window.setTimeout(done, TIMEOUT);
  }

  function setup(root) {
    var menus = (root || document).querySelectorAll('[data-c-menu]');

    Array.prototype.forEach.call(menus, function (details) {
      if (details.dataset.cMenuReady === 'true') {
        return;
      }

      details.dataset.cMenuReady = 'true';

      var summary = details.querySelector('summary');

      if (!summary) {
        return;
      }

      summary.addEventListener('click', function (event) {
        if (!details.open) {
          return;
        }

        event.preventDefault();
        close(details);
      });
    });
  }

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') {
      return;
    }

    Array.prototype.forEach.call(document.querySelectorAll('[data-c-menu][open]'), close);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      setup(document);
      watchTone();
    });
  } else {
    setup(document);
    watchTone();
  }

  document.addEventListener('shopify:section:load', function (event) {
    setup(event.target);
    watchTone();
  });
})();
